import UndoIcon from "@mui/icons-material/Undo";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLoaderData, useNavigate, useParams, useSearchParams } from "react-router-dom";

import DefaultApiService from "../../../../../infrastructure/api/default";
import ProjectWorkflowFoundationApi from "../../../../../infrastructure/api/projectWorkflowFoundation";
import { getApiOrigin } from "../../../../../infrastructure/utils/resolveUploadUrl";
import RequestInfoCard from "../../../../components/request/RequestInfoCard";
import WorkflowV2IntegrationPanel from "../../../../components/request/WorkflowV2IntegrationPanel";
import WorkflowV2SiteVisitDetailsPanel from "../../../../components/request/WorkflowV2SiteVisitDetailsPanel";
import WorkspaceExecutionSection from "./WorkspaceExecutionSection";
import {
  FinancialCoreTab,
  RequestFilesTab,
  RequestFollowUpsTab,
  RequestSummaryTab,
  RequestTechnicalInfoTab,
} from "../../../../components/request/case-tabs";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import { normalizeCheckNewRequestPageData, isRequestCanceled } from "../checkNewRequest/checkNewRequestPageUtils";
import CheckNewRequestModifyPage from "../checkNewRequest/CheckNewRequestModifyPage";
import { CHECK_NEW_REQUEST_CONFIG } from "../checkNewRequest/checkNewRequestConfig";
import { parseAllRequestsDetail } from "../../solarent-operator/all-requests/allRequestsPageUtils";
import { normalizeAssignmentPermissions } from "../../../../components/request/requestAssignmentDisplay";
import {
  PROJECT_WORKSPACE_CONFIG,
  WORKSPACE_SECTIONS,
} from "./projectWorkspaceConfig";
import FollowUpReminderBanner from "../../../../components/request/FollowUpReminderBanner";
import RequestFollowUpDialog from "../../../../components/request/RequestFollowUpDialog";
import useCheckPermission from "../../../../common/hooks/useCheckPermission";
import WorkspaceOperationalBanner from "./WorkspaceOperationalBanner";
import WorkspacePricingSection from "./WorkspacePricingSection";
import WorkspaceSectionNav from "./WorkspaceSectionNav";
import LoanMehrsanStagePanel from "./loanMehrsanStage/LoanMehrsanStagePanel";
import {
  applyWorkflowProgress,
  resolveActiveSectionIndex,
} from "./projectWorkspaceSectionProgress";
import { resolveWorkspaceListBackPath, WORKSPACE_LIST_VIEWS } from "./projectWorkspacePaths";
import {
  mergeLoanMehrsanGateWithRequest,
  normalizeLoanMehrsanStageGate,
  resolveRequestNeedsLoan,
} from "./loanMehrsanStageGateUtils";

const ProjectWorkspacePage = ({ configKey = "solarentOperator" }) => {
  const cfg = PROJECT_WORKSPACE_CONFIG[configKey];
  const canEdit = useCheckPermission("edit");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { id: requestId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const loaderPayload = useLoaderData();

  const explicitSectionParam = searchParams.get("section");
  const listView = searchParams.get("view") || WORKSPACE_LIST_VIEWS.inProcess;
  const initialReviewEditIntent =
    searchParams.get("edit") === "1" ||
    explicitSectionParam === "initial-review";
  const hasExplicitSection =
    Boolean(explicitSectionParam) &&
    WORKSPACE_SECTIONS.some((s) => s.id === explicitSectionParam);

  const [section, setSection] = useState(
    hasExplicitSection ? explicitSectionParam : "overview"
  );
  const [sectionPinnedByUser, setSectionPinnedByUser] = useState(
    initialReviewEditIntent
  );
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [workflowProgressReady, setWorkflowProgressReady] = useState(hasExplicitSection);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [request, setRequest] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [checkNewLoading, setCheckNewLoading] = useState(false);
  const [checkNewError, setCheckNewError] = useState("");
  const [checkNewPayload, setCheckNewPayload] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [workflowStageTitle, setWorkflowStageTitle] = useState("");
  const [loanMehrsanGateApi, setLoanMehrsanGateApi] = useState(null);
  const [loanMehrsanGateLoading, setLoanMehrsanGateLoading] = useState(false);

  const normalizedLoader = useMemo(
    () => normalizeCheckNewRequestPageData(loaderPayload),
    [loaderPayload]
  );

  const respectSelectedSection =
    sectionPinnedByUser || initialReviewEditIntent;

  const workspaceRequest = checkNewPayload?.request ?? request;

  const loanMehrsanGate = useMemo(
    () => mergeLoanMehrsanGateWithRequest(loanMehrsanGateApi, workspaceRequest),
    [loanMehrsanGateApi, workspaceRequest]
  );

  const reloadLoanMehrsanGate = useCallback(async () => {
    if (!requestId) return null;
    setLoanMehrsanGateLoading(true);
    try {
      const res = await ProjectWorkflowFoundationApi.getProjectLoanMehrsanStageGateStatus(
        cfg.source,
        Number(requestId)
      );
      const normalized = normalizeLoanMehrsanStageGate(res?.data ?? res, {
        needsLoanFallback: resolveRequestNeedsLoan(workspaceRequest),
      });
      setLoanMehrsanGateApi(normalized);
      return mergeLoanMehrsanGateWithRequest(normalized, workspaceRequest);
    } catch {
      setLoanMehrsanGateApi(null);
      return mergeLoanMehrsanGateWithRequest(null, workspaceRequest);
    } finally {
      setLoanMehrsanGateLoading(false);
    }
  }, [requestId, cfg.source, workspaceRequest]);

  const canProceedToOperational = loanMehrsanGate?.canProceedToOperational === true;

  const syncWorkspaceSearchParams = useCallback(
    (patch, replace = true) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([key, value]) => {
            if (value == null || value === "") {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          });
          if (!next.get("view")) {
            next.set("view", listView);
          }
          if (next.toString() === prev.toString()) {
            return prev;
          }
          return next;
        },
        { replace }
      );
    },
    [setSearchParams, listView]
  );

  const sectionRef = useRef(section);
  sectionRef.current = section;
  const respectSelectedSectionRef = useRef(respectSelectedSection);
  respectSelectedSectionRef.current = respectSelectedSection;
  const canProceedToOperationalRef = useRef(canProceedToOperational);
  canProceedToOperationalRef.current = canProceedToOperational;

  const refreshWorkflowProgress = useCallback(
    async ({ navigateToStage = true, requestOverride = null } = {}) => {
      if (!requestId) return;

      const res = await ProjectWorkflowFoundationApi.getWorkflowV2StatusSummary(
        cfg.source,
        Number(requestId)
      );

      if (!res.ok) {
        return;
      }

      const stage =
        res.data?.currentStageTitleFa ||
        res.data?.CurrentStageTitleFa ||
        res.data?.currentStageTitle ||
        res.data?.CurrentStageTitle ||
        "";
      setWorkflowStageTitle(String(stage || ""));

      const requestForProgress =
        requestOverride ?? checkNewPayload?.request ?? request;

      const currentSectionId = sectionRef.current;
      const progress = applyWorkflowProgress({
        summary: res.data,
        request: requestForProgress,
        currentSectionId,
        navigateToStage,
        respectExplicitSection: respectSelectedSectionRef.current,
      });
      setActiveStepIndex(progress.activeStepIndex);

      let targetSectionId = progress.sectionId;
      if (
        progress.shouldNavigate &&
        !canProceedToOperationalRef.current &&
        targetSectionId === "operational"
      ) {
        targetSectionId = "loan-mehrsan";
      }

      if (
        progress.shouldNavigate &&
        targetSectionId &&
        targetSectionId !== currentSectionId
      ) {
        setSectionPinnedByUser(false);
        setSection(targetSectionId);
        syncWorkspaceSearchParams({ section: targetSectionId });
      }
    },
    [requestId, cfg.source, request, checkNewPayload, syncWorkspaceSearchParams]
  );

  const navigateToWorkspaceSection = useCallback(
    (sectionId) => {
      setSectionPinnedByUser(true);
      setSection(sectionId);
      syncWorkspaceSearchParams({ section: sectionId });
      setActiveStepIndex(resolveActiveSectionIndex(sectionId));
    },
    [syncWorkspaceSearchParams]
  );

  const reloadWorkspace = useCallback(async () => {
    if (!requestId) return null;
    setDetailLoading(true);
    setDetailError("");
    setCheckNewLoading(true);
    setCheckNewError("");

    let nextRequest = null;
    let nextCheckNewPayload = null;

    try {
      const detailRes = await DefaultApiService.getOneApi(cfg.detailApi, requestId);
      if (detailRes?.isSuccess === false) {
        setDetailError(detailRes?.message || "بارگذاری جزئیات پرونده ناموفق بود.");
      } else {
        const parsed = parseAllRequestsDetail(detailRes);
        nextRequest = parsed.request
          ? normalizeAssignmentPermissions(parsed.request)
          : parsed.request;
        setRequest(nextRequest);
        setFollowUps(parsed.followUps || []);
        setBaseUrl(parsed.baseUrl || getApiOrigin());
      }
    } catch (e) {
      setDetailError(
        e?.response?.data?.message ||
          e?.message ||
          "بارگذاری جزئیات پرونده ناموفق بود."
      );
    } finally {
      setDetailLoading(false);
    }

    try {
      const cnRes = await DefaultApiService.getOneApi(cfg.checkNewApi, requestId);
      if (cnRes?.isSuccess === false) {
        setCheckNewError(cnRes?.message || "بارگذاری فرم بررسی اولیه ناموفق بود.");
      } else {
        nextCheckNewPayload = normalizeCheckNewRequestPageData(cnRes);
        setCheckNewPayload(nextCheckNewPayload);
      }
    } catch (e) {
      setCheckNewError(
        e?.response?.data?.message ||
          e?.message ||
          "بارگذاری فرم بررسی اولیه ناموفق بود."
      );
    } finally {
      setCheckNewLoading(false);
    }

    return {
      request: nextRequest,
      checkNewRequest: nextCheckNewPayload?.request ?? null,
    };
  }, [requestId, cfg.detailApi, cfg.checkNewApi]);

  const handleEmbeddedRefresh = useCallback(async () => {
    const loaded = await reloadWorkspace();
    await refreshWorkflowProgress({
      navigateToStage: true,
      requestOverride: loaded?.checkNewRequest ?? loaded?.request ?? null,
    });
  }, [reloadWorkspace, refreshWorkflowProgress]);

  useEffect(() => {
    reloadLoanMehrsanGate();
  }, [reloadLoanMehrsanGate]);

  useEffect(() => {
    if (loanMehrsanGateLoading || canProceedToOperational || sectionPinnedByUser) {
      return;
    }

    // فقط ورود اولیه به برنامه عملیاتی را محدود کن؛ قیمت/اجرا را به وام/مهرسان برنگردان.
    if (section === "operational") {
      setSection("loan-mehrsan");
      syncWorkspaceSearchParams({ section: "loan-mehrsan" });
    }
  }, [
    loanMehrsanGateLoading,
    canProceedToOperational,
    sectionPinnedByUser,
    section,
    syncWorkspaceSearchParams,
  ]);

  useEffect(() => {
    if (section === "loan-mehrsan") {
      reloadLoanMehrsanGate();
    }
  }, [section, reloadLoanMehrsanGate]);

  useEffect(() => {
    if (hasExplicitSection) {
      setSection(explicitSectionParam);
      // Deep-link / URL section must win over auto-navigation (prevents refresh loops).
      setSectionPinnedByUser(true);
    }
  }, [explicitSectionParam, hasExplicitSection, requestId]);

  useEffect(() => {
    if (!requestId) return;

    let cancelled = false;

    const loadWorkflowProgress = async () => {
      const res = await ProjectWorkflowFoundationApi.getWorkflowV2StatusSummary(
        cfg.source,
        Number(requestId)
      );

      if (cancelled) return;

      if (res.ok) {
        const stage =
          res.data?.currentStageTitleFa ||
          res.data?.CurrentStageTitleFa ||
          res.data?.currentStageTitle ||
          res.data?.CurrentStageTitle ||
          "";
        setWorkflowStageTitle(String(stage || ""));

        const requestForProgress = checkNewPayload?.request ?? request;
        const currentSectionId = sectionRef.current;

        const progress = applyWorkflowProgress({
          summary: res.data,
          request: requestForProgress,
          currentSectionId,
          navigateToStage: true,
          respectExplicitSection: respectSelectedSectionRef.current,
        });
        setActiveStepIndex(progress.activeStepIndex);

        let targetSectionId = progress.sectionId;
        if (
          progress.shouldNavigate &&
          !canProceedToOperationalRef.current &&
          targetSectionId === "operational"
        ) {
          targetSectionId = "loan-mehrsan";
        }

        if (
          progress.shouldNavigate &&
          targetSectionId &&
          targetSectionId !== currentSectionId
        ) {
          setSectionPinnedByUser(false);
          setSection(targetSectionId);
          syncWorkspaceSearchParams({ section: targetSectionId });
        }
      }

      setWorkflowProgressReady(true);
    };

    loadWorkflowProgress();

    return () => {
      cancelled = true;
    };
  }, [
    requestId,
    cfg.source,
    request,
    checkNewPayload,
    syncWorkspaceSearchParams,
  ]);

  useEffect(() => {
    if (normalizedLoader?.request) {
      setRequest(normalizeAssignmentPermissions(normalizedLoader.request));
      setFollowUps(normalizedLoader.followUps || []);
      setBaseUrl(normalizedLoader.baseUrl || getApiOrigin());
    }
  }, [normalizedLoader]);

  useEffect(() => {
    reloadWorkspace();
  }, [requestId, reloadWorkspace]);

  const titleName =
    request?.fullName ||
    [request?.firstName, request?.lastName].filter(Boolean).join(" ") ||
    "—";

  const requestIsCanceled = isRequestCanceled(workspaceRequest) || isRequestCanceled(request);

  const onBackToList = () => {
    navigate(resolveWorkspaceListBackPath(configKey, listView));
  };

  const openFollowUpDialog = useCallback(() => {
    setFollowUpDialogOpen(true);
  }, []);

  const handleFollowUpSubmitted = useCallback(() => {
    setFollowUpDialogOpen(false);
    reloadWorkspace();
  }, [reloadWorkspace]);

  const orchestratorLinks = cfg.orchestratorLinks;

  const renderExecutionSection = () => (
    <WorkspaceExecutionSection
      requestId={Number(requestId)}
      source={cfg.source}
      orchestratorLinks={orchestratorLinks}
      onProgressChanged={refreshWorkflowProgress}
      onGoToFinancial={() => {
        setSection("financial");
        syncWorkspaceSearchParams({ section: "financial" });
      }}
    />
  );

  const renderSectionContent = () => {
    if (!workflowProgressReady && !hasExplicitSection) {
      return (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            تعیین مرحله جاری پرونده…
          </Typography>
        </Box>
      );
    }

    if (detailLoading && !request) {
      return (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      );
    }

    switch (section) {
      case "overview":
        return (
          <Stack spacing={2}>
            {detailError ? (
              <Alert
                severity="warning"
                action={
                  <Button size="small" onClick={reloadWorkspace}>
                    تلاش مجدد
                  </Button>
                }
              >
                {detailError}
              </Alert>
            ) : null}
            <FollowUpReminderBanner
              followUps={followUps}
              rowSummary={request}
              onRegisterClick={canEdit ? openFollowUpDialog : undefined}
              showRegisterButton={canEdit}
              compact
            />
            <RequestSummaryTab request={request} titleName={titleName} />
            <Divider />
            <WorkflowV2IntegrationPanel
              requestId={Number(requestId)}
              source={cfg.source}
              showTimeline
              compact={isMobile}
            />
          </Stack>
        );
      case "initial-review":
        return (
          <Stack spacing={2}>
            {checkNewError ? (
              <Alert
                severity="warning"
                action={
                  <Button size="small" onClick={reloadWorkspace}>
                    تلاش مجدد
                  </Button>
                }
              >
                {checkNewError}
              </Alert>
            ) : null}
            {checkNewLoading && !checkNewPayload ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <CheckNewRequestModifyPage
                config={
                  CHECK_NEW_REQUEST_CONFIG[
                    configKey === "management" ? "management" : "solarentOperator"
                  ]
                }
                embedded
                embeddedPayload={checkNewPayload}
                onRefreshEmbedded={handleEmbeddedRefresh}
                onNavigateToSection={navigateToWorkspaceSection}
                onRegisterFollowUp={canEdit ? openFollowUpDialog : undefined}
                workflowStageTitle={workflowStageTitle}
                workflowSource={cfg.source}
                workspaceDetailApi={cfg.detailApi}
                workspaceListView={listView}
                workspaceEditIntent={initialReviewEditIntent}
              />
            )}
          </Stack>
        );
      case "loan-mehrsan":
        return (
          <LoanMehrsanStagePanel
            requestInfoId={Number(requestId)}
            detailApi={cfg.detailApi}
            workflowSource={cfg.source}
            fileBaseUrl={baseUrl}
            loanMehrsanGate={loanMehrsanGate}
            loanMehrsanGateLoading={loanMehrsanGateLoading}
            canProceedToOperational={canProceedToOperational}
            canEdit={canEdit}
            loanOperationsReadOnly
            onReloadGate={reloadLoanMehrsanGate}
            onNavigateToOperational={() => navigateToWorkspaceSection("operational")}
            onRequestTerminated={reloadWorkspace}
            initialDistributionCompanyId={
              workspaceRequest?.distributionCompanyId ??
              workspaceRequest?.DistributionCompanyId ??
              null
            }
          />
        );
      case "operational":
        return (
          <Stack spacing={2}>
            {detailError ? <Alert severity="warning">{detailError}</Alert> : null}
            <RequestInfoCard
              request={request}
              source={cfg.source}
              followUps={followUps}
              hideWorkflowV2Panel
              workspaceOperationalBundle
            />
          </Stack>
        );
      case "visit":
        return (
          <WorkflowV2SiteVisitDetailsPanel
            requestId={Number(requestId)}
            source={cfg.source}
            showTrigger={false}
          />
        );
      case "quotes-contract":
        return (
          <Stack spacing={2}>
            <WorkspacePricingSection
              requestId={Number(requestId)}
              source={cfg.source}
              onProgressChanged={refreshWorkflowProgress}
              openRecalcOnMount={searchParams.get("recalc") === "1"}
            />
          </Stack>
        );
      case "execution":
        return renderExecutionSection();
      case "financial":
        return (
          <FinancialCoreTab
            requestInfoId={Number(requestId)}
            detailApi={cfg.detailApi}
            fileBaseUrl={baseUrl}
            active
          />
        );
      case "files":
        return (
          <Stack spacing={2}>
            <RequestFilesTab request={request} baseUrl={baseUrl} />
            <RequestTechnicalInfoTab request={request} />
          </Stack>
        );
      case "followups":
        return (
          <RequestFollowUpsTab
            followUps={followUps}
            onRegisterClick={canEdit ? openFollowUpDialog : undefined}
            showRegisterButton={canEdit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Grid container spacing={2} data-testid="operator-workspace">
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={`پرونده پروژه — ${titleName}`}
            subheader={`شناسه درخواست: ${requestId} · کد پیگیری: ${request?.referenceID ?? request?.referenceId ?? "—"}`}
            action={
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<UndoIcon />}
                onClick={onBackToList}
              >
                بازگشت به لیست
              </Button>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Paper variant="outlined" sx={{ position: { md: "sticky" }, top: 16 }}>
                  {!workflowProgressReady ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <WorkspaceSectionNav
                      activeStepIndex={activeStepIndex}
                      selectedSectionId={section}
                      canProceedToOperational={canProceedToOperational}
                      onSelectSection={(sectionId) => {
                        setSectionPinnedByUser(true);
                        setSection(sectionId);
                        syncWorkspaceSearchParams({ section: sectionId });
                      }}
                    />
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={9}>
                <Paper variant="outlined" sx={{ p: isMobile ? 1.5 : 2.5, minHeight: 320 }}>
                  {requestIsCanceled ? (
                    <Alert severity="error" sx={{ mb: 2, fontWeight: 600 }}>
                      این درخواست رد شده
                    </Alert>
                  ) : null}
                  <WorkspaceOperationalBanner
                    requestId={Number(requestId)}
                    source={cfg.source}
                  />
                  {section !== "overview" &&
                  section !== "initial-review" &&
                  section !== "followups" ? (
                    <FollowUpReminderBanner
                      followUps={followUps}
                      rowSummary={request}
                      onRegisterClick={canEdit ? openFollowUpDialog : undefined}
                      showRegisterButton={canEdit}
                      compact
                    />
                  ) : null}
                  {renderSectionContent()}
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <RequestFollowUpDialog
        open={followUpDialogOpen}
        onClose={() => setFollowUpDialogOpen(false)}
        source={cfg.source}
        requestInfoId={Number(requestId)}
        onSubmitted={handleFollowUpSubmitted}
      />
    </Grid>
  );
};

export default ProjectWorkspacePage;
